/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListOrganizationRemoteSessionIssuersRequest,
  ListOrganizationRemoteSessionIssuersSecurity,
  Tier,
} from "../models/operations/listorganizationremotesessionissuers.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationRemoteSessionIssuersInfiniteQuery,
  buildOrganizationRemoteSessionIssuersQuery,
  OrganizationRemoteSessionIssuersInfiniteQueryData,
  OrganizationRemoteSessionIssuersPageParams,
  OrganizationRemoteSessionIssuersQueryData,
  prefetchOrganizationRemoteSessionIssuers,
  prefetchOrganizationRemoteSessionIssuersInfinite,
  queryKeyOrganizationRemoteSessionIssuers,
  queryKeyOrganizationRemoteSessionIssuersInfinite,
} from "./organizationRemoteSessionIssuers.core.js";
export {
  buildOrganizationRemoteSessionIssuersInfiniteQuery,
  buildOrganizationRemoteSessionIssuersQuery,
  type OrganizationRemoteSessionIssuersInfiniteQueryData,
  type OrganizationRemoteSessionIssuersPageParams,
  type OrganizationRemoteSessionIssuersQueryData,
  prefetchOrganizationRemoteSessionIssuers,
  prefetchOrganizationRemoteSessionIssuersInfinite,
  queryKeyOrganizationRemoteSessionIssuers,
  queryKeyOrganizationRemoteSessionIssuersInfinite,
};

export type OrganizationRemoteSessionIssuersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listIssuers organizationRemoteSessionIssuers
 *
 * @remarks
 * List all remote_session_issuers in the caller's organization — organizational (project_id NULL) and project-specific — each with its associated client count and, for project-specific issuers, the owning project name. Requires org:read.
 */
export function useOrganizationRemoteSessionIssuers(
  request?: ListOrganizationRemoteSessionIssuersRequest | undefined,
  security?: ListOrganizationRemoteSessionIssuersSecurity | undefined,
  options?: QueryHookOptions<
    OrganizationRemoteSessionIssuersQueryData,
    OrganizationRemoteSessionIssuersQueryError
  >,
): UseQueryResult<
  OrganizationRemoteSessionIssuersQueryData,
  OrganizationRemoteSessionIssuersQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationRemoteSessionIssuersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listIssuers organizationRemoteSessionIssuers
 *
 * @remarks
 * List all remote_session_issuers in the caller's organization — organizational (project_id NULL) and project-specific — each with its associated client count and, for project-specific issuers, the owning project name. Requires org:read.
 */
export function useOrganizationRemoteSessionIssuersSuspense(
  request?: ListOrganizationRemoteSessionIssuersRequest | undefined,
  security?: ListOrganizationRemoteSessionIssuersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationRemoteSessionIssuersQueryData,
    OrganizationRemoteSessionIssuersQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationRemoteSessionIssuersQueryData,
  OrganizationRemoteSessionIssuersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationRemoteSessionIssuersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listIssuers organizationRemoteSessionIssuers
 *
 * @remarks
 * List all remote_session_issuers in the caller's organization — organizational (project_id NULL) and project-specific — each with its associated client count and, for project-specific issuers, the owning project name. Requires org:read.
 */
export function useOrganizationRemoteSessionIssuersInfinite(
  request?: ListOrganizationRemoteSessionIssuersRequest | undefined,
  security?: ListOrganizationRemoteSessionIssuersSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    OrganizationRemoteSessionIssuersInfiniteQueryData,
    OrganizationRemoteSessionIssuersQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    OrganizationRemoteSessionIssuersInfiniteQueryData,
    OrganizationRemoteSessionIssuersPageParams
  >,
  OrganizationRemoteSessionIssuersQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    OrganizationRemoteSessionIssuersInfiniteQueryData,
    OrganizationRemoteSessionIssuersQueryError,
    InfiniteData<
      OrganizationRemoteSessionIssuersInfiniteQueryData,
      OrganizationRemoteSessionIssuersPageParams
    >,
    QueryKey,
    OrganizationRemoteSessionIssuersPageParams
  >({
    ...buildOrganizationRemoteSessionIssuersInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listIssuers organizationRemoteSessionIssuers
 *
 * @remarks
 * List all remote_session_issuers in the caller's organization — organizational (project_id NULL) and project-specific — each with its associated client count and, for project-specific issuers, the owning project name. Requires org:read.
 */
export function useOrganizationRemoteSessionIssuersInfiniteSuspense(
  request?: ListOrganizationRemoteSessionIssuersRequest | undefined,
  security?: ListOrganizationRemoteSessionIssuersSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    OrganizationRemoteSessionIssuersInfiniteQueryData,
    OrganizationRemoteSessionIssuersQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    OrganizationRemoteSessionIssuersInfiniteQueryData,
    OrganizationRemoteSessionIssuersPageParams
  >,
  OrganizationRemoteSessionIssuersQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    OrganizationRemoteSessionIssuersInfiniteQueryData,
    OrganizationRemoteSessionIssuersQueryError,
    InfiniteData<
      OrganizationRemoteSessionIssuersInfiniteQueryData,
      OrganizationRemoteSessionIssuersPageParams
    >,
    QueryKey,
    OrganizationRemoteSessionIssuersPageParams
  >({
    ...buildOrganizationRemoteSessionIssuersInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setOrganizationRemoteSessionIssuersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      tier?: Tier | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: OrganizationRemoteSessionIssuersQueryData,
): OrganizationRemoteSessionIssuersQueryData | undefined {
  const key = queryKeyOrganizationRemoteSessionIssuers(...queryKeyBase);

  return client.setQueryData<OrganizationRemoteSessionIssuersQueryData>(
    key,
    data,
  );
}

export function invalidateOrganizationRemoteSessionIssuers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      tier?: Tier | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationRemoteSessionIssuers",
      "list",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOrganizationRemoteSessionIssuers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "organizationRemoteSessionIssuers", "list"],
  });
}
